import * as THREE from 'three';
import { PLAYER_COLORS } from '../../../utils/constants.js';
import { colored, vary, seededRandom } from '../../Style.js';
import { hashId } from './style.js';
import { NAVY, bake, hexColor, flagGeometry, waveFlag, hullGeometry } from './units-style.js';

const NEUTRAL_RING = 0x9a9486;
const CHARRED = 0x3e3630;

const DECK_Y = 0.75; // Top of the jetty deck
const WALL_TOP = 2.6;
const HALF_X = 2; // Half-width of the shed
const BACK_Z = -2.1;
const FRONT_Z = 1.5;

// A boathouse: a timber shed on stilts over the water, with a painted roof,
// an open bay holding a dinghy, a slipway down into the lagoon, a lantern by
// the door, a sign board and a flag in the owner's colours.
export class Barracks {
  constructor(scene, barracks) {
    this.scene = scene;
    this.barracks = barracks;
    this.meshGroup = new THREE.Group();
    this.buildingMesh = null;
    this.roofMesh = null;
    this.flagMesh = null;
    this.bannerMesh = null; // Sign board
    this.lanternMesh = null;
    this.rangeMesh = null;
    this.healthBarGroup = null;
    this.healingIndicator = null;
    this.rand = seededRandom(hashId(barracks.id));
    this.wasDestroyed = false;
    this.create();
  }

  create() {
    const neutral = this.barracks.ownerId === -1;

    // Per-boathouse materials so a destroyed one can char and fade alone
    this.structureMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, flatShading: true, vertexColors: true });
    this.teamMaterial = new THREE.MeshStandardMaterial({
      color: this.getPaintColor(),
      roughness: 0.8,
      flatShading: true,
      side: THREE.DoubleSide,
      emissive: this.getColor(),
      emissiveIntensity: neutral ? 0 : 0.12
    });
    this.lanternMaterial = new THREE.MeshStandardMaterial({
      color: NAVY.lamp,
      roughness: 0.8,
      flatShading: true,
      emissive: NAVY.lamp,
      emissiveIntensity: 1.2
    });

    // Stilts, jetty deck and slipway
    const footing = new THREE.Mesh(bake(this.buildJetty()), this.structureMaterial);
    footing.castShadow = true;
    footing.receiveShadow = true;
    this.meshGroup.add(footing);

    // Plank shed with its open bay and the dinghy inside
    this.buildingMesh = new THREE.Mesh(bake(this.buildShed()), this.structureMaterial);
    this.buildingMesh.castShadow = true;
    this.buildingMesh.receiveShadow = true;
    this.meshGroup.add(this.buildingMesh);

    // Gable roof: timber frame plus painted slopes in the owner's colour
    this.roofMesh = new THREE.Mesh(bake(this.buildRoofFrame()), this.structureMaterial);
    this.roofMesh.castShadow = true;
    this.meshGroup.add(this.roofMesh);
    const slopes = new THREE.Mesh(bake(this.buildRoofSlopes()), this.teamMaterial);
    slopes.castShadow = true;
    slopes.receiveShadow = true;
    this.roofMesh.add(slopes);

    // Lantern beside the bay
    this.lanternMesh = new THREE.Mesh(new THREE.OctahedronGeometry(0.16, 0), this.lanternMaterial);
    this.lanternMesh.scale.set(1, 1.4, 1);
    this.lanternMesh.position.set(1.45, DECK_Y + 1.35, FRONT_Z + 0.32);
    this.meshGroup.add(this.lanternMesh);

    // Sign board over the bay
    this.bannerMesh = new THREE.Mesh(bake([
      colored(new THREE.BoxGeometry(2.0, 0.42, 0.08), 0xffffff, { y: WALL_TOP + 0.05, z: FRONT_Z + 0.12 })
    ]), this.teamMaterial);
    this.bannerMesh.castShadow = true;
    this.meshGroup.add(this.bannerMesh);
    this.signTexture = this.createSignTexture();
    this.signMaterial = new THREE.MeshBasicMaterial({ map: this.signTexture, transparent: true, depthWrite: false });
    const lettering = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 0.38), this.signMaterial);
    lettering.position.set(0, WALL_TOP + 0.05, FRONT_Z + 0.17);
    this.bannerMesh.add(lettering);

    // Flag (shows ownership) on a pole at the slipway corner
    this.flagMesh = new THREE.Mesh(flagGeometry(1.5, 0.95), this.teamMaterial);
    this.flagMesh.position.set(2.5, 4.3, 2.4);
    this.flagMesh.castShadow = true;
    this.meshGroup.add(this.flagMesh);

    // Range indicator (shows claim radius)
    const rangeGeometry = new THREE.RingGeometry(7.5, 8, 32);
    const rangeMaterial = new THREE.MeshBasicMaterial({
      color: this.getRingColor(),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.25
    });
    this.rangeMesh = new THREE.Mesh(rangeGeometry, rangeMaterial);
    this.rangeMesh.rotation.x = -Math.PI / 2;
    this.rangeMesh.position.y = 0.05;
    this.meshGroup.add(this.rangeMesh);

    // Health bar background
    this.healthBarGroup = new THREE.Group();
    this.healthBarGroup.position.y = 5.5;

    const healthBarBg = new THREE.Mesh(
      new THREE.BoxGeometry(4, 0.3, 0.1),
      new THREE.MeshBasicMaterial({ color: 0x333333 })
    );
    this.healthBarGroup.add(healthBarBg);

    // Health bar fill
    this.healthBarFill = new THREE.Mesh(
      new THREE.BoxGeometry(4, 0.3, 0.12),
      new THREE.MeshBasicMaterial({ color: 0x00ff00 })
    );
    this.healthBarFill.position.z = 0.01;
    this.healthBarGroup.add(this.healthBarFill);

    this.meshGroup.add(this.healthBarGroup);

    // Healing indicator (glowing cross icon) - hidden by default
    this.healingIndicator = new THREE.Group();
    this.healingIndicator.position.y = 6;
    this.healingIndicator.visible = false;

    // Cross shape using two boxes
    const crossMaterial = new THREE.MeshBasicMaterial({
      color: 0x7ee06a,
      transparent: true,
      opacity: 0.8
    });
    const crossVertical = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 1.2, 0.1),
      crossMaterial
    );
    const crossHorizontal = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 0.3, 0.1),
      crossMaterial
    );
    this.healingIndicator.add(crossVertical);
    this.healingIndicator.add(crossHorizontal);

    // Outer glow ring, a lifebuoy
    const glowGeometry = new THREE.TorusGeometry(0.9, 0.12, 4, 12);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x7ee06a,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });
    this.healingGlow = new THREE.Mesh(glowGeometry, glowMaterial);
    this.healingIndicator.add(this.healingGlow);

    this.meshGroup.add(this.healingIndicator);

    // Position the group
    this.meshGroup.position.set(
      this.barracks.position.x,
      this.barracks.position.y,
      this.barracks.position.z
    );

    this.scene.add(this.meshGroup);
  }

  buildJetty() {
    const rand = this.rand;
    const parts = [];

    // Stilts standing in the water, with weed at the waterline
    for (const x of [-2.6, -0.9, 0.9, 2.6]) {
      for (const z of [-2.6, -0.9, 0.9, 2.2]) {
        parts.push(colored(new THREE.CylinderGeometry(0.11, 0.13, DECK_Y + 0.8, 6), NAVY.woodDark, { x, y: (DECK_Y - 0.8) / 2, z }));
        parts.push(colored(new THREE.CylinderGeometry(0.14, 0.14, 0.14, 6), NAVY.seaweed, { x, y: 0.05, z }));
      }
    }

    // Plank deck, each board its own shade
    for (let i = 0; i < 12; i++) {
      parts.push(colored(new THREE.BoxGeometry(0.46, 0.14, 5.5), vary(NAVY.driftwood, rand, 0.12, 0.02), {
        x: -2.75 + i * 0.5, y: DECK_Y - 0.07, z: -0.2
      }));
    }
    for (const z of [-2.85, 2.45]) {
      parts.push(colored(new THREE.BoxGeometry(6, 0.18, 0.2), NAVY.woodDark, { y: DECK_Y - 0.14, z }));
    }

    // Slipway rails running down into the lagoon
    const slope = Math.atan2(DECK_Y + 0.4, 1.0);
    for (const x of [-0.55, 0.55]) {
      parts.push(colored(new THREE.BoxGeometry(0.16, 0.12, 1.3), NAVY.woodDark, { x, y: DECK_Y / 2 - 0.2, z: 2.55, rx: slope }));
    }
    for (let i = 0; i < 4; i++) {
      parts.push(colored(new THREE.BoxGeometry(1.4, 0.06, 0.16), NAVY.wood, { y: DECK_Y - 0.12 - i * 0.28, z: 2.15 + i * 0.25 }));
    }

    // Bollards and a coil of rope on the deck
    for (const x of [-2.5, 2.5]) {
      parts.push(colored(new THREE.CylinderGeometry(0.12, 0.14, 0.3, 6), NAVY.iron, { x, y: DECK_Y + 0.15, z: 2.2 }));
    }
    parts.push(colored(new THREE.TorusGeometry(0.2, 0.07, 4, 8), NAVY.rope, { x: -2.2, y: DECK_Y + 0.05, z: 1.9, rx: Math.PI / 2 }));

    // Flag pole
    parts.push(colored(new THREE.CylinderGeometry(0.07, 0.1, 3.9, 6), NAVY.woodDark, { x: 2.5, y: 2.35, z: 2.4 }));
    parts.push(colored(new THREE.SphereGeometry(0.14, 6, 4), NAVY.brass, { x: 2.5, y: 4.35, z: 2.4 }));
    return parts;
  }

  buildShed() {
    const rand = this.rand;
    const parts = [];
    const height = WALL_TOP - DECK_Y;
    const midY = DECK_Y + height / 2;
    const depth = FRONT_Z - BACK_Z;
    const midZ = (FRONT_Z + BACK_Z) / 2;

    // Weatherboard walls: horizontal boards stepping out slightly
    for (let i = 0; i < 6; i++) {
      const y = DECK_Y + 0.15 + i * 0.3;
      const shade = vary(NAVY.wood, rand, 0.1, 0.01);
      for (const sx of [-1, 1]) {
        parts.push(colored(new THREE.BoxGeometry(0.1, 0.32, depth), shade, { x: sx * HALF_X, y, z: midZ, rz: sx * 0.06 }));
      }
      parts.push(colored(new THREE.BoxGeometry(HALF_X * 2, 0.32, 0.1), shade, { y, z: BACK_Z, rx: -0.06 }));
    }
    // Front wall either side of the bay, and the dark interior
    for (const sx of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.75, height, 0.12), NAVY.woodLight, { x: sx * 1.62, y: midY, z: FRONT_Z }));
    }
    parts.push(colored(new THREE.BoxGeometry(2.5, 0.35, 0.14), NAVY.woodLight, { y: WALL_TOP - 0.18, z: FRONT_Z }));
    parts.push(colored(new THREE.BoxGeometry(HALF_X * 2 - 0.1, height, 0.05), 0x2a221a, { y: midY, z: BACK_Z + 0.08 }));
    // Corner posts and a white-painted door frame
    for (const sx of [-1, 1]) {
      for (const z of [BACK_Z, FRONT_Z]) {
        parts.push(colored(new THREE.BoxGeometry(0.22, height + 0.1, 0.22), NAVY.woodDark, { x: sx * HALF_X, y: midY, z }));
      }
      parts.push(colored(new THREE.BoxGeometry(0.12, height - 0.4, 0.16), NAVY.white, { x: sx * 1.22, y: midY - 0.18, z: FRONT_Z + 0.04 }));
    }
    // Small window on each side
    for (const sx of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.12, 0.45, 0.6), NAVY.white, { x: sx * (HALF_X + 0.05), y: DECK_Y + 1.1, z: -0.3 }));
      parts.push(colored(new THREE.BoxGeometry(0.13, 0.33, 0.48), 0x2f4a5a, { x: sx * (HALF_X + 0.06), y: DECK_Y + 1.1, z: -0.3 }));
    }
    // A dinghy on its cradle in the bay, half out of the door
    parts.push(hullGeometry({
      stern: -0.9, bow: 1.0, beam: 0.9, top: 0.32, keel: -0.12, sheer: 0.12, transom: 0.7,
      side: NAVY.white, trim: NAVY.wood, bottom: NAVY.bottom, sections: 7
    }).translate(0, DECK_Y + 0.15, 1.0));
    parts.push(colored(new THREE.BoxGeometry(0.9, 0.12, 0.2), NAVY.woodDark, { y: DECK_Y + 0.06, z: 0.5 }));
    parts.push(colored(new THREE.BoxGeometry(0.9, 0.12, 0.2), NAVY.woodDark, { y: DECK_Y + 0.06, z: 1.5 }));
    // Lantern bracket
    parts.push(colored(new THREE.BoxGeometry(0.06, 0.06, 0.32), NAVY.iron, { x: 1.45, y: DECK_Y + 1.6, z: FRONT_Z + 0.2 }));
    return parts;
  }

  // Gable ends and ridge, the timber of the roof
  buildRoofFrame() {
    const rise = 1.1;
    const parts = [];
    const gable = new THREE.Shape();
    gable.moveTo(-HALF_X - 0.05, 0);
    gable.lineTo(HALF_X + 0.05, 0);
    gable.lineTo(0, rise);
    gable.closePath();
    for (const z of [BACK_Z, FRONT_Z]) {
      parts.push(colored(new THREE.ExtrudeGeometry(gable, { depth: 0.12, bevelEnabled: false }), NAVY.woodLight, { y: WALL_TOP, z: z - 0.06 }));
    }
    parts.push(colored(new THREE.BoxGeometry(0.2, 0.2, FRONT_Z - BACK_Z + 0.9), NAVY.woodDark, {
      y: WALL_TOP + rise + 0.08, z: (FRONT_Z + BACK_Z) / 2 + 0.15
    }));
    // Barge boards along the front gable
    for (const sx of [-1, 1]) {
      const length = Math.hypot(HALF_X + 0.45, rise + 0.3);
      parts.push(colored(new THREE.BoxGeometry(length, 0.14, 0.1), NAVY.white, {
        x: sx * (HALF_X + 0.45) / 2, y: WALL_TOP + rise / 2 - 0.05, z: FRONT_Z + 0.55, rz: -sx * Math.atan2(rise + 0.3, HALF_X + 0.45)
      }));
    }
    return parts;
  }

  // Two painted roof slopes, ridge along Z, overhanging the bay at the front
  buildRoofSlopes() {
    const rise = 1.1;
    const span = HALF_X + 0.45;
    const pitch = Math.atan2(rise + 0.3, span);
    const slopeLength = Math.hypot(span, rise + 0.3);
    const length = FRONT_Z - BACK_Z + 0.9;
    const parts = [];
    for (const sx of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(slopeLength, 0.12, length), 0xffffff, {
        x: sx * span / 2, y: WALL_TOP + rise / 2 - 0.05, z: (FRONT_Z + BACK_Z) / 2 + 0.15, rz: -sx * pitch
      }));
    }
    return parts;
  }

  // White painted lettering, laid over the team-coloured board
  createSignTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 52;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 30px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('BOATHOUSE', canvas.width / 2, canvas.height / 2 + 2);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  getColor() {
    if (this.barracks.ownerId === -1) {
      return 0x888888; // Gray for neutral
    }
    return hexColor(PLAYER_COLORS[this.barracks.ownerId]);
  }

  getPaintColor() {
    return this.barracks.ownerId === -1 ? NAVY.neutralPaint : this.getColor();
  }

  getRingColor() {
    return this.barracks.ownerId === -1 ? NEUTRAL_RING : this.getColor();
  }

  update(barracksData, time) {
    const ownerChanged = this.barracks.ownerId !== barracksData.ownerId;
    this.barracks = barracksData;

    // Repaint if the owner changed
    if (ownerChanged) {
      this.teamMaterial.color.setHex(this.getPaintColor());
      this.teamMaterial.emissive.setHex(this.getColor());
      this.teamMaterial.emissiveIntensity = this.barracks.ownerId === -1 ? 0 : 0.12;
      if (this.rangeMesh) {
        this.rangeMesh.material.color.setHex(this.getRingColor());
      }
    }

    // Wave the flag
    if (this.flagMesh) {
      waveFlag(this.flagMesh, time, 0.15);
    }

    // Handle destroyed state
    const destroyed = !!this.barracks.isDestroyed;
    if (destroyed !== this.wasDestroyed) {
      this.wasDestroyed = destroyed;
      this.setDestroyedLook(destroyed);
    }

    if (destroyed) {
      // Show respawn timer through opacity
      this.structureMaterial.opacity = 0.3 + 0.2 * Math.sin(time * 4);
    } else {
      // Lantern flickers
      this.lanternMaterial.emissiveIntensity = 1.1 + Math.sin(time * 9) * 0.12 + Math.sin(time * 13.7) * 0.08;

      // Pulse range indicator for neutral barracks
      if (this.barracks.ownerId === -1 && this.rangeMesh) {
        const pulse = 0.15 + Math.sin(time * 2) * 0.1;
        this.rangeMesh.material.opacity = pulse;
      } else if (this.rangeMesh) {
        this.rangeMesh.material.opacity = 0.25;
      }
    }

    // Update health bar
    this.updateHealthBar();

    // Update healing indicator based on occupant count
    this.updateHealingIndicator(time);
  }

  // Charred, sagging and roofless while destroyed; restored on respawn.
  setDestroyedLook(destroyed) {
    this.structureMaterial.color.setHex(destroyed ? CHARRED : 0xffffff);
    this.structureMaterial.transparent = destroyed;
    this.structureMaterial.opacity = 1.0;
    this.structureMaterial.depthWrite = !destroyed;
    this.structureMaterial.needsUpdate = true;
    this.buildingMesh.rotation.z = destroyed ? 0.08 : 0;
    this.roofMesh.visible = !destroyed;
    this.flagMesh.visible = !destroyed;
    this.bannerMesh.visible = !destroyed;
    this.lanternMesh.visible = !destroyed;
    this.rangeMesh.material.opacity = destroyed ? 0.1 : 0.25;
  }

  updateHealingIndicator(time) {
    if (!this.healingIndicator) return;

    const hasOccupants = this.barracks.occupantCount > 0;
    this.healingIndicator.visible = hasOccupants && !this.barracks.isDestroyed;

    if (hasOccupants && !this.barracks.isDestroyed) {
      // Pulse the indicator
      const pulse = 0.6 + Math.sin(time * 4) * 0.4;
      this.healingIndicator.children.forEach(child => {
        if (child.material) {
          child.material.opacity = pulse;
        }
      });

      // Rotate the glow
      if (this.healingGlow) {
        this.healingGlow.rotation.z = time * 2;
      }

      // Scale pulse
      const scale = 1 + Math.sin(time * 3) * 0.1;
      this.healingIndicator.scale.set(scale, scale, scale);
    }
  }

  updateHealthBar() {
    if (!this.healthBarFill) return;

    const healthPercent = this.barracks.health / this.barracks.maxHealth;
    this.healthBarFill.scale.x = Math.max(0.01, healthPercent);
    this.healthBarFill.position.x = (healthPercent - 1) * 2; // Shift left as it decreases

    // Change color based on health
    if (healthPercent > 0.6) {
      this.healthBarFill.material.color.setHex(0x00ff00); // Green
    } else if (healthPercent > 0.3) {
      this.healthBarFill.material.color.setHex(0xffff00); // Yellow
    } else {
      this.healthBarFill.material.color.setHex(0xff0000); // Red
    }

    // Hide health bar when at full health
    this.healthBarGroup.visible = healthPercent < 1;
  }

  // Check if unit is in claiming range
  isInRange(position) {
    const dx = position.x - this.barracks.position.x;
    const dz = position.z - this.barracks.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.barracks.claimRadius * this.barracks.claimRadius;
  }

  // Check if barracks can be claimed by player
  canBeClaimed(playerId) {
    if (this.barracks.isDestroyed) return false;
    if (this.barracks.ownerId === playerId) return false;
    return true;
  }

  remove() {
    this.scene.remove(this.meshGroup);
    // Every material here belongs to this boathouse
    const materials = new Set();
    this.meshGroup.traverse(child => {
      if (!child.isMesh) return;
      child.geometry.dispose();
      materials.add(child.material);
    });
    materials.forEach(material => material.dispose());
    this.signTexture.dispose();
  }
}
